import React, { useEffect, useState } from "react";
import portrait from "./assets/chidinma-onuora.jpg";
import "./App.css";

const projects = [
  {
    number: "01",
    name: "Partna",
    category: "Payments infrastructure",
    description:
      "Designed and built transaction systems powering crypto onramp and offramp.",
    stack: ["Go", "PostgreSQL", "Redis", "Docker"],
    link: "https://getpartna.com/",
    accent: "lime",
  },
  {
    number: "02",
    name: "Atlaxchange",
    category: "Fiat on/off-ramp",
    description:
      "Worked on KYC and KYB onboarding, plus the services that handle fiat and crypto deposits.",
    stack: ["Go", "PostgreSQL", "Docker", "REST APIs"],
    link: "https://www.atlaxchange.com/",
    accent: "violet",
  },
  {
    number: "03",
    name: "Coinprofile",
    category: "Card issuing",
    description: "Led the backend work for a USD card-issuing service.",
    stack: ["Node.js", "Express", "MongoDB", "Docker"],
    link: "https://coinprofile.com/",
    accent: "orange",
  },
];

const experience = [
  {
    period: "2023 — Now",
    role: "Software Engineer",
    company: "Partna",
    summary:
      "I build Go services for fiat and crypto flows, work with integration partners and make internal tools for the operations team.",
  },
  {
    period: "2024 — 2025",
    role: "Go Engineer · Contract",
    company: "Atlaxchange",
    summary:
      "I built parts of the KYC and KYB onboarding flow and integrated fiat and crypto deposit providers.",
  },
  {
    period: "2022 — 2023",
    role: "Software Engineering Intern",
    company: "Coinprofile",
    summary:
      "I built a USD card-issuing service, refactored legacy Node.js code, added automated tests and wrote API documentation.",
  },
  {
    period: "2022",
    role: "Full-Stack Developer Intern",
    company: "GoMyCode",
    summary:
      "I built full-stack applications and REST APIs, then set up CI/CD for deployment.",
  },
];

const capabilities = [
  "Go",
  "Node.js",
  "TypeScript",
  "PostgreSQL",
  "MongoDB",
  "Redis",
  "Docker",
  "Kubernetes",
  "AWS",
  "API design",
  "Distributed systems",
  "System reliability",
  "Technical writing",
];

const expertise = [
  "Backend engineering",
  "Fintech infrastructure",
  "API design",
  "Reliable systems",
];

const articles = [
  {
    number: "01",
    topic: "Distributed systems",
    title:
      "Transactional State Machines for Agentic Finance: Orchestrating Asynchronous Cross-Border Rails with the Saga Pattern",
    description:
      "Using sagas and explicit state machines to coordinate long-running cross-border transactions.",
    link: "https://medium.com/@chidinma21/transactional-state-machines-for-agentic-finance-orchestrating-asynchronous-cross-border-rails-4a528c3ae0dd",
  },
  {
    number: "02",
    topic: "Redis",
    title: "Practical Redis Usage for Engineers: Beyond Caching GET Requests",
    description:
      "Redis beyond caching. Patterns for rate limits, fraud checks, background jobs and idempotency.",
    link: "https://medium.com/@chidinma21/practical-redis-usage-for-engineers-beyond-caching-get-requests-241241952476",
  },
  {
    number: "03",
    topic: "Security",
    title: "Authentication vs. Authorization",
    description:
      "A simple breakdown of identity checks, permissions and access-control flow.",
    link: "https://medium.com/@chidinma21/authentication-vs-authorization-74bec5d9030f",
  },
];

function ArrowUpRight() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M5 15 15 5M7 5h8v8" />
    </svg>
  );
}

function Mark() {
  return (
    <a className="brand" href="#top" aria-label="Chidinma Onuora, home">
      <span>CO</span>
      <i aria-hidden="true" />
    </a>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState("idle");

  useEffect(() => {
    const closeMenu = () => setMenuOpen(false);
    window.addEventListener("resize", closeMenu);
    return () => window.removeEventListener("resize", closeMenu);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const handleEnquirySubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const requiredFields = ["name", "email", "message"];

    requiredFields.forEach((fieldName) => {
      const field = form.elements[fieldName];
      field.value = field.value.trim();
      field.setCustomValidity(
        field.value ? "" : "Please complete this field before sending.",
      );
    });

    if (!form.checkValidity()) {
      form.reportValidity();
      setFormStatus("idle");
      return;
    }

    const formData = new FormData(form);

    setFormStatus("submitting");

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });

      if (!response.ok) {
        throw new Error("The enquiry could not be sent.");
      }

      form.reset();
      setFormStatus("success");
    } catch (error) {
      setFormStatus("error");
    }
  };

  return (
    <div className="site-shell" id="top">
      <header className="site-header">
        <Mark />

        <button
          className="menu-button"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span>{menuOpen ? "Close" : "Menu"}</span>
          <i aria-hidden="true" />
        </button>

        <nav
          className={`site-nav ${menuOpen ? "is-open" : ""}`}
          id="main-navigation"
          aria-label="Primary navigation"
        >
          <a href="#work" onClick={closeMenu}>
            Work
          </a>
          <a href="#about" onClick={closeMenu}>
            About
          </a>
          <a href="#experience" onClick={closeMenu}>
            Experience
          </a>
          <a href="#writing" onClick={closeMenu}>
            Blog
          </a>
          <a href="#contact" onClick={closeMenu}>
            Contact
          </a>
        </nav>

        <a
          className="header-link"
          href="/resume.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Résumé <ArrowUpRight />
        </a>
      </header>

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-copy">
            {/* <div className="availability">
              <i aria-hidden="true" />
              Backend systems
            </div> */}

            <p className="eyebrow">Based in Lagos, Nigeria</p>
            <h1 id="hero-title">
              Hi, I’m Chidinma. I’m a <em>software engineer.</em>
            </h1>
            <p className="hero-intro">
              I’m a product-minded software engineer who takes end-to-end
              ownership. I spend most of my time writing Go, building APIs,
              microservices, and event-driven distributed systems. I’ve worked
              primarily in fintech, with experience building systems around
              financial regulations, reliability, and correctness. I’m also
              proficient with AI agents, prompt engineering, and agentic
              development workflows.
            </p>

            <div className="hero-actions">
              <a className="button button-primary" href="#work">
                Explore my work <ArrowUpRight />
              </a>
              <a className="text-link" href="#contact">
                Let’s work together
              </a>
            </div>

            <dl className="hero-facts">
              <div>
                <dt>5</dt>
                <dd>
                  Years building
                  <br />
                  production software
                </dd>
              </div>
            </dl>
          </div>

          <div className="hero-portrait-wrap">
            <div className="portrait-frame">
              <img src={portrait} alt="Chidinma Onuora" className="portrait" />
              <div className="portrait-shade" aria-hidden="true" />
            </div>
          </div>

          <div className="scroll-cue" aria-hidden="true">
            <span>Scroll to explore</span>
            <i />
          </div>
        </section>

        <div className="ticker" aria-label="Areas of expertise">
          <div className="ticker-track">
            {[0, 1].map((group) => (
              <div
                className="ticker-group"
                aria-hidden={group === 1 ? "true" : undefined}
                key={group}
              >
                {expertise.concat(expertise).map((item, index) => (
                  <React.Fragment key={`${group}-${item}-${index}`}>
                    <span>{item}</span>
                    <i aria-hidden="true">✦</i>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>

        <section
          className="section projects-section"
          id="work"
          aria-labelledby="work-title"
        >
          <div className="section-heading">
            <h2 className="section-kicker" id="work-title">
              01 · Selected work
            </h2>
          </div>

          <div className="projects-list">
            {projects.map((project) => (
              <article
                className={`project-card ${project.accent}`}
                key={project.name}
              >
                <div className="project-topline">
                  <span>{project.number}</span>
                  <span>{project.category}</span>
                </div>
                <div className="project-body">
                  <h3>{project.name}</h3>
                  <p>{project.description}</p>
                </div>
                <div className="project-footer">
                  <ul aria-label={`${project.name} technologies`}>
                    {project.stack.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit ${project.name}`}
                  >
                    <ArrowUpRight />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          className="section about-section"
          id="about"
          aria-labelledby="about-title"
        >
          <div className="about-label">
            <h2 className="section-kicker" id="about-title">
              02 · About
            </h2>
            <div className="mini-portrait">
              <img src={portrait} alt="" />
            </div>
          </div>

          <div className="about-copy">
            <div className="about-columns">
              <p>
                I like understanding the problem properly, keeping the solution
                simple and writing code the next person won’t hate me for. That
                usually means clear APIs, predictable behaviour, useful tests
                and systems that are straightforward to maintain.
              </p>
              <p>
                Away from my laptop, I’m usually reading fiction, designing and
                making an outfit, or trying something new in the kitchen. I’ve
                always liked making things; software is just one version of
                that.
              </p>
            </div>
          </div>
        </section>

        <section
          className="section experience-section"
          id="experience"
          aria-labelledby="experience-title"
        >
          <div className="section-heading compact">
            <h2 className="section-kicker" id="experience-title">
              03 · Experience
            </h2>
          </div>

          <div className="experience-list">
            {experience.map((item, index) => (
              <article
                className="experience-row"
                key={`${item.company}-${item.role}`}
              >
                <span className="experience-number">0{index + 1}</span>
                <p className="experience-period">{item.period}</p>
                <div>
                  <h3>{item.role}</h3>
                  <p>{item.company}</p>
                </div>
                <p className="experience-summary">{item.summary}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="capabilities" aria-labelledby="capabilities-title">
          <div className="capabilities-header">
            <h2 className="section-kicker" id="capabilities-title">
              04 · Toolkit
            </h2>
          </div>
          <ul>
            {capabilities.map((capability, index) => (
              <li key={capability}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {capability}
              </li>
            ))}
          </ul>
        </section>

        <section
          className="section writing-section"
          id="writing"
          aria-labelledby="writing-title"
        >
          <div className="writing-heading">
            <h2 className="section-kicker" id="writing-title">
              05 · Blog
            </h2>
            <a
              href="https://medium.com/@chidinma21"
              target="_blank"
              rel="noopener noreferrer"
            >
              All articles <ArrowUpRight />
            </a>
          </div>

          <div className="articles-list">
            {articles.map((article) => (
              <a
                className="article-card"
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                key={article.title}
              >
                <div className="article-meta">
                  <span>{article.number}</span>
                  <span>{article.topic}</span>
                </div>
                <h3>{article.title}</h3>
                <div className="article-footer">
                  <p>{article.description}</p>
                  <span className="article-arrow" aria-hidden="true">
                    <ArrowUpRight />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section
          className="contact-section"
          id="contact"
          aria-labelledby="contact-title"
        >
          <div className="contact-copy">
            <h2 className="section-kicker" id="contact-title">
              06 · Contact
            </h2>
            <p>Want to work together? Let’s talk.</p>
          </div>

          {formStatus === "success" ? (
            <div className="contact-success" role="status">
              <span aria-hidden="true">✓</span>
              <h3>Thanks! Your enquiry has been sent.</h3>
              <p>I’ll get back to you as soon as I can.</p>
              <button
                className="text-link"
                type="button"
                onClick={() => setFormStatus("idle")}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              className="contact-form"
              name="contact"
              method="POST"
              data-netlify="true"
              onSubmit={handleEnquirySubmit}
            >
              <input type="hidden" name="form-name" value="contact" />
              <label>
                <span>Your name</span>
                <input
                  type="text"
                  name="name"
                  placeholder="Jane Smith"
                  onInput={(event) => event.currentTarget.setCustomValidity("")}
                  required
                />
              </label>
              <label>
                <span>Email address</span>
                <input
                  type="email"
                  name="email"
                  placeholder="jane@company.com"
                  onInput={(event) => event.currentTarget.setCustomValidity("")}
                  required
                />
              </label>
              <label>
                <span>Tell me about the project</span>
                <textarea
                  name="message"
                  rows="3"
                  placeholder="A little context goes a long way..."
                  onInput={(event) => event.currentTarget.setCustomValidity("")}
                  required
                />
              </label>
              {formStatus === "error" && (
                <p className="form-status form-status-error" role="alert">
                  Something went wrong. Please try again.
                </p>
              )}
              <button
                className="button button-primary"
                type="submit"
                disabled={formStatus === "submitting"}
              >
                {formStatus === "submitting" ? "Sending…" : "Send enquiry"}
                {formStatus !== "submitting" && <ArrowUpRight />}
              </button>
            </form>
          )}
        </section>
      </main>

      <footer className="site-footer">
        <Mark />
        <p>Chidinma Onuora · Backend Engineer</p>
        <div className="social-links">
          <a
            href="https://github.com/chidinma21"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub
          </a>
          <a
            href="https://www.linkedin.com/in/chidinmaonuora1/"
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn
          </a>
          <a
            href="https://medium.com/@chidinma21"
            target="_blank"
            rel="noopener noreferrer"
          >
            Medium
          </a>
        </div>
        <a className="back-to-top" href="#top">
          Back to top ↑
        </a>
      </footer>
    </div>
  );
}

export default App;
